import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { COUNTRIES, GENDERS, INTERESTS, OCCUPATIONS, REFERRAL_SOURCES } from "../data/formOptions";

const passwordChecks = (p) => ({
  length: p.length >= 12,
  upper: /[A-Z]/.test(p),
  number: /\d/.test(p),
  special: /[^A-Za-z0-9]/.test(p),
});

export default function PersonForm({ refCode = "", referrer = null }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    gender: "Prefer not to say",
    phone: "",
    date_of_birth: "",
    country: "United States",
    province_state: "",
    city: "",
    postal_code: "",
    occupation: "Volunteer",
    organization_affiliation: "",
    primary_interests: [],
    profile_visibility: "public",
    referral_source: refCode && referrer ? "Partner Referral" : "Google Search",
    accept_terms: false,
    newsletter_opt_in: true,
  });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value }));
  const toggleInterest = (it) =>
    setForm((f) => ({
      ...f,
      primary_interests: f.primary_interests.includes(it)
        ? f.primary_interests.filter((x) => x !== it)
        : [...f.primary_interests, it],
    }));

  const pw = passwordChecks(form.password);
  const pwOk = Object.values(pw).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.accept_terms) {
      setError("You must accept the Terms of Service & Privacy Policy.");
      return;
    }
    if (!pwOk) {
      setError("Password does not meet all requirements.");
      return;
    }
    if (form.password !== form.confirm_password) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/register-person", { ...form, ref_code: refCode || null });
      navigate("/verify", {
        state: {
          kind: "person",
          session_id: data.session_id,
          email: data.email,
          dev_otp: data.dev_otp,
        },
      });
    } catch (e2) {
      setError(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="csc-card p-6 lg:p-8 space-y-5" data-testid="person-register-form">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-[0.32em] text-[#39ff14]">02 / People</div>
          <h2 className="mt-1.5 text-2xl lg:text-3xl font-semibold text-white">Join as an Individual</h2>
          <p className="text-sm text-[#94a3b8] mt-1">Volunteers, donors, IT pros, students &amp; allies.</p>
        </div>
        <div
          className="hidden md:block h-10 w-10 rounded-md border border-[rgba(0,170,255,0.18)]"
          style={{ boxShadow: "inset 0 0 12px rgba(57, 255, 20,0.25)" }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">First Name *</label>
          <input data-testid="person-first-name" required className="csc-input" value={form.first_name} onChange={update("first_name")} />
        </div>
        <div>
          <label className="csc-label">Last Name *</label>
          <input data-testid="person-last-name" required className="csc-input" value={form.last_name} onChange={update("last_name")} />
        </div>
      </div>

      <div>
        <label className="csc-label">Email Address *</label>
        <input data-testid="person-email" required type="email" className="csc-input" value={form.email} onChange={update("email")} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">Password *</label>
          <input data-testid="person-password" required type="password" className="csc-input" value={form.password} onChange={update("password")} />
        </div>
        <div>
          <label className="csc-label">Confirm Password *</label>
          <input data-testid="person-confirm-password" required type="password" className="csc-input" value={form.confirm_password} onChange={update("confirm_password")} />
        </div>
      </div>

      {form.password.length > 0 && (
        <ul className="font-mono-tech text-[11px] grid grid-cols-2 gap-1 -mt-2" data-testid="password-rules">
          {[
            ["length", "12+ characters"],
            ["upper", "Uppercase letter"],
            ["number", "A number"],
            ["special", "Special character"],
          ].map(([k, label]) => (
            <li key={k} className={pw[k] ? "text-[#39ff14]" : "text-[#94a3b8]"}>
              {pw[k] ? "✓" : "○"} {label}
            </li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">Gender</label>
          <select className="csc-input" value={form.gender} onChange={update("gender")} data-testid="person-gender">
            {GENDERS.map((g) => (<option key={g} value={g}>{g}</option>))}
          </select>
        </div>
        <div>
          <label className="csc-label">Phone</label>
          <input className="csc-input" value={form.phone} onChange={update("phone")} data-testid="person-phone" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">Date of Birth</label>
          <input type="date" className="csc-input" value={form.date_of_birth} onChange={update("date_of_birth")} data-testid="person-dob" />
        </div>
        <div>
          <label className="csc-label">Country *</label>
          <select required className="csc-input" value={form.country} onChange={update("country")} data-testid="person-country">
            {COUNTRIES.map((c) => (<option key={c} value={c}>{c}</option>))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="csc-label">Province / State</label>
          <input className="csc-input" value={form.province_state} onChange={update("province_state")} data-testid="person-state" />
        </div>
        <div>
          <label className="csc-label">City / Town</label>
          <input className="csc-input" value={form.city} onChange={update("city")} data-testid="person-city" />
        </div>
        <div>
          <label className="csc-label">Postal Code</label>
          <input className="csc-input" value={form.postal_code} onChange={update("postal_code")} data-testid="person-postal" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">Occupation / Role *</label>
          <select className="csc-input" value={form.occupation} onChange={update("occupation")} data-testid="person-occupation">
            {OCCUPATIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
          </select>
        </div>
        <div>
          <label className="csc-label">Organization Affiliation</label>
          <input className="csc-input" value={form.organization_affiliation} onChange={update("organization_affiliation")} data-testid="person-org-affil" />
        </div>
      </div>

      <div>
        <label className="csc-label">Primary Interests</label>
        <div className="flex flex-wrap gap-2" data-testid="person-interests">
          {INTERESTS.map((it) => {
            const active = form.primary_interests.includes(it);
            return (
              <button
                type="button"
                key={it}
                onClick={() => toggleInterest(it)}
                className={`csc-pill ${active ? "csc-pill-active" : ""}`}
                data-testid={`interest-${it.replace(/\W+/g, "-").toLowerCase()}`}
              >
                {it}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="csc-label">Profile Visibility</label>
          <div className="flex gap-3 pt-1 font-mono-tech text-xs uppercase tracking-wider">
            {["public", "private"].map((v) => (
              <label key={v} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="visibility"
                  value={v}
                  checked={form.profile_visibility === v}
                  onChange={() => setForm((f) => ({ ...f, profile_visibility: v }))}
                  className="accent-[#00aaff]"
                  data-testid={`visibility-${v}`}
                />
                <span className={form.profile_visibility === v ? "text-[#00aaff]" : "text-[#94a3b8]"}>{v}</span>
              </label>
            ))}
          </div>
        </div>
        <div>
          <label className="csc-label">Referral Source</label>
          <select className="csc-input" value={form.referral_source} onChange={update("referral_source")} data-testid="person-referral">
            {REFERRAL_SOURCES.map((r) => (<option key={r} value={r}>{r}</option>))}
          </select>
        </div>
      </div>

      <div className="space-y-2 pt-1">
        <label className="flex items-start gap-2.5 text-sm text-[#94a3b8] cursor-pointer">
          <input type="checkbox" checked={form.accept_terms} onChange={update("accept_terms")} className="mt-1 accent-[#00aaff]" required data-testid="person-accept-terms" />
          <span>
            I agree to the <a href="/terms" className="csc-link">Terms of Service</a> and{" "}
            <a href="/privacy" className="csc-link">Privacy Policy</a>.
          </span>
        </label>
        <label className="flex items-start gap-2.5 text-sm text-[#94a3b8] cursor-pointer">
          <input type="checkbox" checked={form.newsletter_opt_in} onChange={update("newsletter_opt_in")} className="mt-1 accent-[#39ff14]" data-testid="person-newsletter" />
          <span>Send me security tips, updates &amp; opportunities.</span>
        </label>
      </div>

      {error && (
        <div className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 rounded px-3 py-2" data-testid="person-form-error">
          {error}
        </div>
      )}

      <button type="submit" disabled={submitting} className="csc-btn-secondary w-full" data-testid="person-submit">
        {submitting ? "Sending Code…" : "Create Account & Send OTP"}
      </button>
    </form>
  );
}
